import { useState } from 'react';
import { Plus, X, Trash2 } from 'lucide-react';
import { createChecklist } from '../../lib/checklists';
import { useBusiness } from '../../contexts/BusinessContext';

interface Props {
    onClose: () => void;
    onChecklistCreated: (checklist: { id: string; name: string }) => void;
}

export function CreateChecklistModal({ onClose, onChecklistCreated }: Props) {
    const { businessId } = useBusiness();
    const [name, setName] = useState('');
    const [items, setItems] = useState<{ title: string }[]>([{ title: '' }]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleAddItem = () => {
        setItems([...items, { title: '' }]);
    };

    const handleRemoveItem = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const handleItemChange = (index: number, title: string) => {
        const newItems = [...items];
        newItems[index] = { title };
        setItems(newItems);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!businessId) {
            setError('No business ID available');
            return;
        }

        try {
            setIsSubmitting(true);
            setError(null);
            // Filter out empty items
            const validItems = items.filter(item => item.title.trim() !== '');
            
            // Create the checklist
            const checklist = await createChecklist({
                business_id: businessId,
                name: name.trim(),
                items: validItems,
            });

            // Notify parent of the created checklist
            onChecklistCreated({
                id: checklist.id,
                name: checklist.name,
            });
        } catch (error) {
            console.error('Failed to create checklist:', error);
            setError('Failed to create checklist. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg relative z-[100]">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Create Custom Checklist</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                            Checklist Name
                        </label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Enter checklist name"
                            required
                        />
                    </div>

                    <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">Items</label>
                        {items.map((item, index) => (
                            <div key={index} className="flex gap-2">
                                <input
                                    type="text"
                                    value={item.title}
                                    onChange={(e) => handleItemChange(index, e.target.value)}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Enter item title"
                                    required
                                />
                                {items.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveItem(index)}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <Trash2 className="h-5 w-5" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={handleAddItem}
                        className="mt-3 flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                        <Plus className="h-4 w-4" />
                        Add Item
                    </button>

                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 hover:text-gray-900"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Checklist'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
} 